import React, { useState, useEffect, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import { Alert, Avatar, Box, Card, CardContent, Divider, FormControl, FormControlLabel, IconButton, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import useSuspenseFetch from '../hooks/useSuspenseFetch';
import HelpIcon from '@mui/icons-material/Help';
import { colours } from '../theme/constants';
import ArchiveToggle from './selects/ArchiveToggle';
import Groups2Icon from '@mui/icons-material/Groups2';

const ProofManualList = ({ emailAddresses, setEmailAddresses, emailAddressesLimit = null, saveAsProofGroup, setSaveAsProofGroup }) => {
    const [value, setValue] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [input, setInput] = useState(null);
    const { showSnackbar } = useSnackbar();
    const { handleGet } = useApi();
    const { data: account } = useSuspenseFetch('account');
    const { allowedRecipients } = account;
    const isInternalComms = account?.licence?.isInternalComms ?? false;
    const [accountUsers, setAccountUsers] = useState(account.users);
    const [selectedAccountUsers, setSelectedAccountUsers] = useState([]);
    const onRefChange = useCallback(node => setInput(node), []);

    const handleValidateEmail = async value => {
        //html5 form validate pattern
        const isValidPattern = input.checkValidity();

        //pattern / empty string check
        if (value === '' || !isValidPattern) {
            showSnackbar('Please enter a valid email address', 'error');
            return null;
        }

        const emailAddress = value.toLowerCase();

        //check for match
        const match = emailAddresses.find(e => e.toLowerCase() === emailAddress);

        if (match) {
            showSnackbar('Duplicate email address not added', 'warning');
            return null;
        }

        // allowed recipients pattern match?
        if (isInternalComms && allowedRecipients.length > 0) {
            const mailbox = emailAddress.trim();
            const domain = mailbox.split('@')[1];

            const matches = allowedRecipients.filter(e => e.type === 'mailbox' ? mailbox === e.pattern ? true : false : domain === e.pattern ? true : false);

            if (matches.length === 0) {
                showSnackbar('You can only add recipients whose email address adheres to one of the allowed patterns in your account.', 'error');
                setValue('');
                return;
            }
        }

        setIsValidating(true);

        let isValid = false;

        //check if email is unsubscribed or suppressed
        const response = await handleGet(`contacts/email/${emailAddress}`);

        //if found, check if unsub/suppressed
        if (response.status === 200) {
            const contact = await response.json();

            if (contact.isSuppressed === false && contact.isUnsubscribed === false) {
                isValid = true;
            }
            else {
                showSnackbar(`${emailAddress} cannot be sent to. They might be unsubscribed or suppressed`, 'error');
            }
        }
        else if (response.status === 404) {
            //if 404, all good
            isValid = true;
        }
        else if (response.status === 400) {
            const json = await response.json();
            showSnackbar(json.message, 'error');
        }
        else {
            showSnackbar('An error occurred, pelase try again', 'error');
        }

        setValue('');
        setIsValidating(false);

        input.focus();

        if (isValid) {
            return emailAddress;
        }

        return null;
    };

    const handleValidateAccountEmail = async emailAddress => {
        let isValid = false;

        //check if email is unsubscribed or suppressed
        const response = await handleGet(`contacts/email/${emailAddress}`);

        //if found, check if unsub/suppressed
        if (response.status === 200) {
            const contact = await response.json();

            if (contact.isSuppressed === false && contact.isUnsubscribed === false) {
                isValid = true;
            }
        }
        else if (response.status === 404) {
            //if 404, all good
            isValid = true;
        }
        else if (response.status === 400) {
            const json = await response.json();
            showSnackbar(json.message, 'error');
        }
        else {
            showSnackbar('An error occurred, pelase try again', 'error');
        }

        return isValid;
    };

    const handleAddEmails = newEmailAddresses => {
        setEmailAddresses([...emailAddresses, ...newEmailAddresses]);
    };

    const handleSubmit = async ({ event, value }) => {
        event && event.preventDefault();

        const email = event ? event.target.value : value;

        const validEmail = await handleValidateEmail(email);

        validEmail && handleAddEmails([validEmail]);
    };

    const handleRemoveEmailAddress = (email) => {
        const updatedUsers = selectedAccountUsers.map(user =>
            user.emailAddress === email
                ? { ...user, checked: false }
                : user
        );

        setSelectedAccountUsers(updatedUsers);
        setEmailAddresses(emailAddresses?.filter(e => e !== email));
    };

    const handleGetAccountUsers = async () => {
        const validatedUsers = [];

        for (const userData of account.users) {
            const isValidEmail = await handleValidateAccountEmail(userData.emailAddress);

            validatedUsers.push({
                ...userData,
                checked: false,
                valid: isValidEmail
            });
        }

        setAccountUsers(validatedUsers);
        setSelectedAccountUsers(validatedUsers
            .filter(user => !emailAddresses.includes(user.emailAddress))
        );

    };

    const handleAccountUsersAddToList = (id) => {
        const updatedUsers = selectedAccountUsers.map(user =>
            user.id === id ? { ...user, checked: !user.checked } : user
        );

        setSelectedAccountUsers(updatedUsers);

        const toggledUser = selectedAccountUsers.find(user => user.id === id);

        if (!toggledUser) return;

        const email = toggledUser.emailAddress;
        const wasChecked = toggledUser.checked;

        setEmailAddresses(prev => {
            if (wasChecked) {
                return prev.filter(e => e !== email);
            }
            else {
                return prev.includes(email) ? prev : [...prev, email];
            }
        });
    };

    const handlePaste = async event => {
        //get data from clipboard and split by common delimiters
        //comma space semi-colon tab line-break
        const data = (event.clipboardData || window.clipboardData)
            .getData('text')
            .toLowerCase()
            .split(/,| |;|\t|\r\n/)
            .filter(e => e !== '');

        //validate by running each value through the input
        const validEmails = [];

        for (let i = 0; i < data.length; i++) {
            setValue(data[i]);
            const validEmail = await handleValidateEmail(data[i]);
            validEmail && validEmails.push(validEmail);
        }

        validEmails.length > 0 && handleAddEmails(validEmails);
    };

    const onTheLimit = emailAddressesLimit ? emailAddresses.length === emailAddressesLimit : false;
    const exceededLimit = emailAddressesLimit ? emailAddresses.length > emailAddressesLimit : false;
    const limitDifference = emailAddresses.length - emailAddressesLimit;
    const disabled = isValidating || onTheLimit || exceededLimit;

    useEffect(() => {
        const filtered = accountUsers.filter(
            user => !emailAddresses.includes(user.emailAddress)
        );
        setSelectedAccountUsers(filtered);
    }, [emailAddresses, accountUsers]);

    useEffect(() => {
        handleGetAccountUsers();
    }, []);

    return (
        <Grid
            container
            spacing={2}
            alignItems="stretch"
            sx={{ mb: 1 }}
        >
            <Grid size={{ xs: 12, md: 4 }}>
                <Card
                    sx={{
                        borderRadius: 4,
                        height: '100%'
                    }}
                >
                    <CardContent
                        sx={{
                            p: 1,
                            '&:last-child': {
                                paddingBottom: 2
                            }
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    p: 1,
                                    fontWeight: 'bold',
                                    color: colours.primary
                                }}
                            >
                                Account users to send to
                            </Typography>
                            <Tooltip
                                placement="right-end"
                                arrow
                                title={(
                                    <Typography
                                        sx={{ pt: 1 }}
                                        component="span"
                                        variant="body2"
                                    >
                                        An unsubscribed or suppressed User cannot be added to Recipients.
                                    </Typography>
                                )}
                            >
                                <HelpIcon color="info" fontSize="small" sx={{ cursor: 'pointer' }} />
                            </Tooltip>
                        </Box>
                        <Box
                            sx={{
                                height: '20vh',
                                overflowY: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: '6px'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                    borderRadius: '3px'
                                }
                            }}
                        >
                            {selectedAccountUsers.filter(user => !user.checked).length === 0 ? (
                                <Alert
                                    icon={<CheckIcon fontSize="inherit" />}
                                    severity="success"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 4
                                    }}
                                >
                                    All Account Users added to Recipients
                                </Alert>
                            ) : (
                                <List>
                                    {selectedAccountUsers
                                        .filter(user => !user.checked)
                                        .map((r, i) => {
                                            const isdisabled = r.valid === false;

                                            return (
                                                <ListItem
                                                    key={i}
                                                    sx={{
                                                        p: 0
                                                    }}
                                                >
                                                    <ListItemButton
                                                        onClick={() => handleAccountUsersAddToList(r.id)}
                                                        disabled={isdisabled}
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar
                                                                sx={{
                                                                    width: 35,
                                                                    height: 35
                                                                }}
                                                                alt={r.name}
                                                                src={r.avatarURL}
                                                            />
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={
                                                                r.name
                                                            }
                                                            secondary={r.emailAddress}
                                                            sx={{
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-all'
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                </List>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
                <Card
                    sx={{
                        borderRadius: 4,
                        height: '100%'
                    }}
                >
                    <CardContent
                        sx={{
                            pt: 2,
                            px: 2,
                            '&:last-child': {
                                paddingBottom: 0
                            }
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Groups2Icon
                                sx={{
                                    fontSize: 24,
                                    color: colours.primary,
                                    ml: 1
                                }}
                            />
                            <Typography
                                variant="h6"
                                color = {colours.primary}
                                sx={{
                                    alignItems: 'center',
                                    fontWeight: 'bold'
                                }}
                            >
                                Recipients
                            </Typography>
                            <Tooltip
                                placement="right-end"
                                arrow
                                title={(
                                    <Typography
                                        component="span"
                                        variant="body2"
                                    >
                                            A maximum of {emailAddressesLimit} recipients may be sent a proof.
                                    </Typography>
                                )}
                            >
                                <HelpIcon color="info" fontSize="small" sx={{ cursor: 'pointer' }} />
                            </Tooltip>
                        </Box>
                        <Paper variant="outlined"
                            sx={{
                                borderRadius: 4,
                                overflow: 'hidden',
                                mt: 2
                            }}
                        >
                            <TextField
                                error={exceededLimit}
                                disabled={disabled}
                                inputRef={onRefChange}
                                placeholder={
                                    exceededLimit
                                        ? `Limit exceeded, please remove ${limitDifference} address${limitDifference > 1 ? 'es' : ''}`
                                        : 'Click here to add email address'
                                }
                                type="email"
                                fullWidth
                                value={value}
                                onChange={event => setValue(event.target.value)}
                                onKeyUp={event => (event.key === 'Enter' || event.key === ',' || event.key === ' ' || event.key === ';') && handleSubmit({ event })}
                                onPaste={handlePaste}
                                variant="standard"
                                slotProps={{
                                    input: {
                                        disableUnderline: true,
                                        endAdornment: (
                                            <InputAdornment sx={{ pr: 1 }} position="end">
                                                <IconButton
                                                    onClick={() => handleSubmit({ value: input.value })}
                                                    disabled={disabled}
                                                    edge="end"
                                                    sx={{
                                                        borderRadius: 3,
                                                        backgroundColor: colours.primary,
                                                        color: colours.primarySelected,
                                                        '&:hover': {
                                                            backgroundColor: colours.secondary
                                                        }
                                                    }}
                                                >
                                                    {isValidating ? <CircularProgress size={16} /> : <AddIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            px: 2
                                        }
                                    }
                                }}
                            />
                            <Divider />
                            <Box
                                sx={{
                                    height: '10vh',
                                    boxSizing: 'border-box',
                                    p: 1,
                                    overflowY: 'auto',
                                    mx: 1,
                                    mt: 1,
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignContent: 'flex-start',
                                    gap: 1,
                                    borderRadius: 3,
                                    '&::-webkit-scrollbar': {
                                        width: '6px'
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                                        borderRadius: '3px',
                                        boxShadow: 'inset 0 2px 0 rgba(0, 0, 0, 0)'
                                    }
                                }}
                            >
                                {emailAddresses.map(email => (
                                    <Chip
                                        key={email}
                                        label={email}
                                        onDelete={() => handleRemoveEmailAddress(email)}
                                        sx={{ borderRadius: 3, maxWidth: '100%' }}
                                    />
                                ))}
                            </Box>
                        </Paper>
                        <FormControl
                            sx={{ ml: 1 }}
                        >
                            <FormControlLabel
                                control={(
                                    <ArchiveToggle
                                        checked={saveAsProofGroup}
                                        onChange={() => setSaveAsProofGroup(!saveAsProofGroup)}
                                    />
                                )}
                                label="Save these recipients as your Proof Group"
                            />
                        </FormControl>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

export default ProofManualList;